import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { environment } from '@environments/environment';
import { UnauthorizedHandlerService } from '@core/services/unauthorized-handler.service';
import { UnauthorizedError } from '@core/errors/http-errors';
import { SseEventPayload } from 'portal-shared';

interface SseChannel {
  endpoint: string;
  events$: Subject<SseEventPayload>;
  connected$: Subject<void>;
  abortController: AbortController | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectAttempts: number;
}

const MAX_RECONNECT_ATTEMPTS = 3;
const MAX_RECONNECT_BACKOFF_MS = 8000;

@Injectable({
  providedIn: 'root',
})
export class SseService implements OnDestroy {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly unauthorizedHandler = inject(UnauthorizedHandlerService);
  private readonly baseUrl = environment.apiBaseUrl;

  private destroyed = false;

  private readonly admin = this.createChannel('/sse/admin');
  private readonly analyst = this.createChannel('/sse/analyst');

  readonly adminEvents: Observable<SseEventPayload> =
    this.admin.events$.asObservable();
  readonly analystEvents: Observable<SseEventPayload> =
    this.analyst.events$.asObservable();
  readonly adminConnected: Observable<void> =
    this.admin.connected$.asObservable();
  readonly analystConnected: Observable<void> =
    this.analyst.connected$.asObservable();

  connectAdmin(): void {
    this.admin.reconnectAttempts = 0;
    void this.connect(this.admin);
  }

  connectAnalyst(): void {
    this.analyst.reconnectAttempts = 0;
    void this.connect(this.analyst);
  }

  disconnectAdmin(): void {
    this.disconnectChannel(this.admin);
  }

  disconnectAnalyst(): void {
    this.disconnectChannel(this.analyst);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disconnectChannel(this.admin);
    this.disconnectChannel(this.analyst);
    this.admin.events$.complete();
    this.analyst.events$.complete();
    this.admin.connected$.complete();
    this.analyst.connected$.complete();
  }

  private createChannel(endpoint: string): SseChannel {
    return {
      endpoint,
      events$: new Subject<SseEventPayload>(),
      connected$: new Subject<void>(),
      abortController: null,
      reconnectTimer: null,
      reconnectAttempts: 0,
    };
  }

  private async connect(channel: SseChannel): Promise<void> {
    if (channel.abortController || this.destroyed) return;

    channel.abortController = new AbortController();

    try {
      for await (const event of this.streamSse(channel.endpoint, {
        signal: channel.abortController.signal,
        onConnected: () => {
          channel.reconnectAttempts = 0; // healthy connection — reset the budget
          channel.connected$.next();
        },
      })) {
        channel.events$.next(event);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // 401 already triggered the logout flow — don't fight it with reconnects.
      if (error instanceof UnauthorizedError) {
        channel.abortController = null;
        return;
      }
      this.scheduleReconnect(channel);
      return;
    }

    channel.abortController = null;
    if (!this.destroyed) this.scheduleReconnect(channel);
  }

  private scheduleReconnect(channel: SseChannel): void {
    channel.abortController = null;
    if (this.destroyed) return;

    if (channel.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(
        `SSE ${channel.endpoint}: giving up after ${MAX_RECONNECT_ATTEMPTS} failed reconnect attempts`,
      );
      return;
    }

    // Exponential backoff: 1s, 2s, 4s … capped at MAX_RECONNECT_BACKOFF_MS.
    const backoff = Math.min(
      1000 * 2 ** channel.reconnectAttempts,
      MAX_RECONNECT_BACKOFF_MS,
    );
    channel.reconnectAttempts++;
    channel.reconnectTimer = setTimeout(() => void this.connect(channel), backoff);
  }

  private disconnectChannel(channel: SseChannel): void {
    if (channel.reconnectTimer !== null) {
      clearTimeout(channel.reconnectTimer);
      channel.reconnectTimer = null;
    }
    if (channel.abortController) {
      channel.abortController.abort();
      channel.abortController = null;
    }
  }

  /**
   * Streams server-sent events for an endpoint. SSE runs on `fetch` (not
   * HttpClient), so it bypasses the HTTP interceptors and builds its own auth
   * header and 401 handling here. Errors propagate to `connect()`, which
   * reconnects on anything other than an abort or a 401 (which starts logout).
   */
  private async *streamSse(
    endpoint: string,
    options: { signal: AbortSignal; onConnected: () => void },
  ): AsyncGenerator<SseEventPayload> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: await this.buildHeaders(),
      signal: options.signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.unauthorizedHandler.handle(); // Throws UnauthorizedError
      }
      throw new Error(`SSE connection failed with status ${response.status}`);
    }

    options.onConnected();

    if (!response.body) return;

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(new EventSourceParserStream())
      .getReader();

    try {
      while (true) {
        const { done, value: event } = await reader.read();
        if (done) return;
        try {
          yield JSON.parse(event.data) as SseEventPayload;
        } catch {
          // ignore malformed frames
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private async buildHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    try {
      const token = await firstValueFrom(
        this.oidcSecurityService.getAccessToken(),
      );
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.debug('Could not retrieve access token for SSE:', error);
    }
    return headers;
  }
}
